import { useEffect, useState } from "react";
import { api, unwrap } from "../api/client";

/** regional pack seen by the maps: its basemap and its bounding box [minLon, minLat, maxLon, maxLat] */
export interface PackArea { basemapUrl?: string | null; bbox: number[] }

let cache: Promise<PackArea[]> | null = null;

/** packs of the manifest (`GET /packs`, public), read once per page load */
export function loadPackAreas(): Promise<PackArea[]> {
  cache ??= api.GET("/packs").then((r) => {
    const m = unwrap(r).manifest;
    return (m?.packs ?? []).map((p) => {
      const b = p.files.find((f) => f.kind === "basemap");
      return { basemapUrl: b ? `/packs/${b.path}` : undefined, bbox: p.bbox };
    });
  }).catch(() => { cache = null; return []; });
  return cache;
}

export function usePackAreas(): PackArea[] {
  const [packs, setPacks] = useState<PackArea[]>([]);
  useEffect(() => { let on = true; void loadPackAreas().then((p) => { if (on) setPacks(p); }); return () => { on = false; }; }, []);
  return packs;
}

/**
 * Pack under a position: among the bounding boxes containing it (neighbouring departments overlap),
 * the one whose centre is the closest. Null outside every pack.
 */
export function packUnder(packs: PackArea[], lon: number, lat: number): PackArea | null {
  let best: PackArea | null = null;
  let bestD = Infinity;
  for (const p of packs) {
    const b = p.bbox;
    if (!b || b.length < 4 || lon < b[0] || lon > b[2] || lat < b[1] || lat > b[3]) continue;
    const d = (lon - (b[0] + b[2]) / 2) ** 2 + (lat - (b[1] + b[3]) / 2) ** 2;
    if (d < bestD) { bestD = d; best = p; }
  }
  return best;
}

/** basemap to show: the pack given by the page (event, sandbox), otherwise the pack under the view centre */
export function basemapAt(basemapUrl: string | null | undefined, packs: PackArea[] | undefined, lon: number, lat: number): string | null {
  if (basemapUrl) return basemapUrl;
  return (packs && packUnder(packs, lon, lat)?.basemapUrl) || null;
}

/** the view centre is outside the mapped area: the bounding box of the given pack, otherwise every known pack (SW-DP-04) */
export function outsideAt(bbox: number[] | null | undefined, packs: PackArea[] | undefined, lon: number, lat: number): boolean {
  if (bbox && bbox.length >= 4) return lon < bbox[0] || lon > bbox[2] || lat < bbox[1] || lat > bbox[3];
  return !!packs && packs.length > 0 && packUnder(packs, lon, lat) === null;
}
